import { createClient } from '@supabase/supabase-js';
import { getErrorMessage } from './errorMessages';
import { supabase } from './supabase';

// API configuration
const API_CONFIG = {
  timeout: 30000, // 30 seconds
  retries: 3,
  retryDelay: 1000,
};

// Retry utility function
async function withRetry<T>(
  fn: () => Promise<T>,
  retries: number = API_CONFIG.retries
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.retryDelay));
      return withRetry(fn, retries - 1);
    }
    throw error;
  }
}

// Base function for Edge Functions calls
async function callEdgeFunction<T = any>(
  functionName: string,
  options: {
    body?: any;
    headers?: Record<string, string>;
    method?: string;
    searchParams?: Record<string, string>;
  } = {}
): Promise<T> {
  try {
    // Get session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No active session');
    }

    // Configure headers
    const headers = {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Determine base URL
    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!baseUrl) {
      throw new Error('VITE_SUPABASE_URL is not configured');
    }

    // Build URL with query params
    let url = `${baseUrl}/functions/v1/${functionName}`;
    if (options.searchParams) {
      const params = new URLSearchParams(options.searchParams);
      url += `?${params.toString()}`;
    }

    // Configure timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    try {
      // Log request details for debugging
      console.debug('Edge Function Request:', {
        url,
        method: options.method || 'POST',
        body: options.body,
      });

      const response = await withRetry(async () => {
        const res = await fetch(url, {
          method: options.method || 'POST',
          headers,
          body: options.body ? JSON.stringify(options.body) : undefined,
          signal: controller.signal,
        });

        const data = await res.json().catch(() => ({
          error: { message: `HTTP error! status: ${res.status}` }
        }));

        if (!res.ok) {
          throw new Error(data.error?.message || `HTTP error! status: ${res.status}`);
        }

        return data;
      });

      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Edge Function Error:', {
      functionName,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    throw error;
  }
}

// Search cities with improved error handling and validation
export async function searchCities(query: string): Promise<Array<{ name: string; state: string }>> {
  try {
    // Validate input
    if (!query || query.length < 2) {
      return [];
    }

    // Add retry logic for network issues
    return await withRetry(async () => {
      const result = await callEdgeFunction<Array<{ name: string; state: string }>>('cities', {
        method: 'GET',
        searchParams: { q: query },
      });

      return result || [];
    }, 3); // Try up to 3 times
  } catch (error) {
    console.error('Error in searchCities:', error);
    throw new Error('Não foi possível buscar as cidades. Por favor, tente novamente.');
  }
}

// Delete user with improved error handling
export async function deleteUser(userId: string) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const result = await callEdgeFunction<{ success: boolean }>('delete-user', {
      method: 'DELETE',
      body: { userId },
    });

    if (!result?.success) {
      throw new Error('Failed to delete user');
    }

    return result;
  } catch (error) {
    console.error('Error in deleteUser:', error);
    throw new Error(getErrorMessage(error));
  }
}

// Reset user password with improved error handling
export async function resetUserPassword(userId: string) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const result = await callEdgeFunction<{ success: boolean }>('reset-password', {
      method: 'POST',
      body: { userId },
    });

    if (!result?.success) {
      throw new Error('Failed to reset password');
    }

    return result;
  } catch (error) {
    console.error('Error in resetUserPassword:', error);
    throw new Error(getErrorMessage(error));
  }
}